import React, { useState, useEffect } from 'react';
import { 
  X, 
  Send, 
  MapPin, 
  AlertTriangle, 
  Clock, 
  Phone, 
  CheckCircle2, 
  Bell, 
  Radio, 
  History,
  MessageSquare,
  Sparkles
} from 'lucide-react';
import { RoteiroOperacionalAtivo } from '../../types/operacional';
import { rastreamentoNotificacaoService, NotificacaoItem } from '../../services/rastreamentoNotificacaoService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  roteiro: RoteiroOperacionalAtivo | null;
  onNotificacaoEnviada?: (mensagem: string) => void;
}

export const NotificarMotoristaModal: React.FC<Props> = ({
  isOpen,
  onClose,
  roteiro,
  onNotificacaoEnviada
}) => {
  const [tipo, setTipo] = useState<'info' | 'alerta' | 'solicitacao_localizacao' | 'urgente'>('solicitacao_localizacao');
  const [titulo, setTitulo] = useState('📍 Solicitação de Localização GPS');
  const [mensagem, setMensagem] = useState('Central Operacional solicita atualização imediata da sua localização GPS em tempo real.');
  const [enviando, setEnviando] = useState(false);
  const [sucessoMsg, setSucessoMsg] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'enviar' | 'historico'>('enviar');
  const [historico, setHistorico] = useState<NotificacaoItem[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);

  // Define templates rápidos
  const handleSelecionarTemplate = (
    tipoTemplate: 'solicitacao_localizacao' | 'alerta' | 'info' | 'urgente',
    tituloTemplate: string,
    msgTemplate: string
  ) => {
    setTipo(tipoTemplate);
    setTitulo(tituloTemplate);
    setMensagem(msgTemplate);
  };

  useEffect(() => {
    if (isOpen && roteiro) {
      setSucessoMsg(null);
      // Carrega histórico de mensagens desse motorista
      carregarHistorico();
    }
  }, [isOpen, roteiro]);

  const carregarHistorico = async () => {
    if (!roteiro) return;
    setCarregandoHistorico(true);
    try {
      const items = await rastreamentoNotificacaoService.getNotificacoesMotorista(roteiro.motorista_id);
      setHistorico(items);
    } finally {
      setCarregandoHistorico(false);
    }
  };

  if (!isOpen || !roteiro) return null;

  const handleEnviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!titulo.trim() || !mensagem.trim()) return;

    setEnviando(true);
    setSucessoMsg(null);
    try {
      const res = await rastreamentoNotificacaoService.enviarNotificacao({
        pessoa_id: roteiro.motorista_id,
        titulo: titulo.trim(),
        mensagem: mensagem.trim(),
        tipo,
        link_redirecionamento: '/area-motorista/roteiro'
      });

      if (res.success) {
        setSucessoMsg(tipo === 'solicitacao_localizacao' 
          ? 'Solicitação de GPS enviada com sucesso ao motorista!' 
          : 'Notificação transmitida em tempo real!'
        );
        onNotificacaoEnviada?.(mensagem);
        carregarHistorico();
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        alert('Erro ao enviar notificação: ' + (res.error || 'Falha no servidor'));
      }
    } catch (err: any) {
      alert('Falha ao enviar: ' + err.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
        {/* Cabeçalho */}
        <div className="bg-slate-900 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center text-white shadow-md">
              <Bell className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-bold text-base leading-tight">Central de Notificação ao Motorista</h3>
              <p className="text-xs text-slate-300">
                {roteiro.motorista_nome} • <span className="font-mono">{roteiro.veiculo_placa}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Abas */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-4 pt-2 gap-2 text-xs">
          <button
            onClick={() => setActiveTab('enviar')}
            className={`pb-2.5 px-3 font-bold flex items-center gap-1.5 border-b-2 transition ${
              activeTab === 'enviar'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <Send className="h-3.5 w-3.5" />
            <span>Enviar Notificação / GPS</span>
          </button>
          <button
            onClick={() => setActiveTab('historico')}
            className={`pb-2.5 px-3 font-bold flex items-center gap-1.5 border-b-2 transition ${
              activeTab === 'historico'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <History className="h-3.5 w-3.5" />
            <span>Histórico ({historico.length})</span>
          </button>
        </div>

        {/* Conteúdo */}
        <div className="p-4 overflow-y-auto flex-1 space-y-4">
          {sucessoMsg && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs font-semibold flex items-center gap-2 animate-in fade-in">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>{sucessoMsg}</span>
            </div>
          )}

          {activeTab === 'enviar' ? (
            <form onSubmit={handleEnviar} className="space-y-4">
              {/* Atalhos Rápidos */}
              <div>
                <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-2 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                  Modelos Rápidos de Transmissão
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleSelecionarTemplate(
                      'solicitacao_localizacao',
                      '📍 Solicitação de Localização GPS',
                      'A Central Operacional solicita a atualização imediata da sua localização em tempo real. Toque para transmitir sinal.'
                    )}
                    className={`p-2.5 rounded-xl border text-left text-xs transition flex items-start gap-2 ${
                      tipo === 'solicitacao_localizacao'
                        ? 'bg-red-50 border-red-300 text-red-900 ring-1 ring-red-400'
                        : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                    }`}
                  >
                    <MapPin className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">Solicitar Localização</p>
                      <p className="text-[10px] text-slate-500">Pede sinal GPS imediato</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSelecionarTemplate(
                      'alerta',
                      '⚠️ Alerta na Rota / Trânsito',
                      'Atenção motorista: Trânsito intenso ou lentidão reportada na sua rota. Mantenha cautela e acompanhe pelo app.'
                    )}
                    className={`p-2.5 rounded-xl border text-left text-xs transition flex items-start gap-2 ${
                      tipo === 'alerta'
                        ? 'bg-amber-50 border-amber-300 text-amber-900 ring-1 ring-amber-400'
                        : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                    }`}
                  >
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">Alerta de Trânsito</p>
                      <p className="text-[10px] text-slate-500">Aviso sobre rota</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSelecionarTemplate(
                      'urgente',
                      '⚡ Prioridade na Entrega',
                      'Favor priorizar a próxima parada deste roteiro. Cliente aguardando no local.'
                    )}
                    className={`p-2.5 rounded-xl border text-left text-xs transition flex items-start gap-2 ${
                      tipo === 'urgente'
                        ? 'bg-purple-50 border-purple-300 text-purple-900 ring-1 ring-purple-400'
                        : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                    }`}
                  >
                    <Clock className="h-4 w-4 text-purple-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">Priorizar Parada</p>
                      <p className="text-[10px] text-slate-500">Urgência com cliente</p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSelecionarTemplate(
                      'info',
                      '📞 Contato com a Central',
                      'Por gentileza, entre em contato via ligação ou WhatsApp com o setor operacional assim que estacionar.'
                    )}
                    className={`p-2.5 rounded-xl border text-left text-xs transition flex items-start gap-2 ${
                      tipo === 'info'
                        ? 'bg-blue-50 border-blue-300 text-blue-900 ring-1 ring-blue-400'
                        : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                    }`}
                  >
                    <Phone className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">Ligue para a Central</p>
                      <p className="text-[10px] text-slate-500">Contato operacional</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Título */}
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Título da Notificação
                </label>
                <input
                  type="text"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  required
                  placeholder="Ex: 📍 Solicitação de Localização GPS"
                  className="w-full text-xs px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:outline-none"
                />
              </div>

              {/* Mensagem */}
              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1">
                  Mensagem para o Motorista
                </label>
                <textarea
                  rows={3}
                  value={mensagem}
                  onChange={(e) => setMensagem(e.target.value)}
                  required
                  placeholder="Digite a mensagem ou instrução para o motorista..."
                  className="w-full text-xs p-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:outline-none resize-none"
                />
                <span className="text-[10px] text-slate-400 text-right block">
                  {mensagem.length}/300 caracteres
                </span>
              </div>

              {/* Status do envio */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/80 text-[11px] text-slate-600 flex items-center gap-2">
                <Radio className="h-4 w-4 text-emerald-600 animate-pulse shrink-0" />
                <span>
                  A notificação é salva no banco estrutural (<strong className="text-slate-800 font-semibold">notificacoes</strong>) e aparece instantaneamente na tela do motorista.
                </span>
              </div>

              {/* Ações */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={enviando || !mensagem.trim()}
                  className="px-5 py-2 text-xs font-bold bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-md transition flex items-center gap-2 disabled:opacity-50"
                >
                  {enviando ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Transmitindo...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-3.5 w-3.5" />
                      <span>{tipo === 'solicitacao_localizacao' ? 'Solicitar Localização Agora' : 'Transmitir Notificação'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            /* Aba Histórico */
            <div className="space-y-2">
              {carregandoHistorico ? (
                <div className="p-8 text-center text-xs text-slate-500">
                  Carregando histórico...
                </div>
              ) : historico.length === 0 ? (
                <div className="p-8 text-center text-xs text-slate-400">
                  Nenhuma notificação enviada para este motorista ainda.
                </div>
              ) : (
                historico.map((item) => {
                  const dataFormatada = new Date(item.created_at).toLocaleString('pt-BR');
                  return (
                    <div
                      key={item.id}
                      className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-1"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-slate-800">{item.titulo}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          item.lida ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {item.lida ? 'Lida' : 'Aguardando'}
                        </span>
                      </div>
                      <p className="text-slate-600 text-[11px] leading-relaxed">{item.mensagem}</p>
                      <div className="text-[10px] text-slate-400 flex items-center justify-between pt-1">
                        <span>{dataFormatada}</span>
                        <span className="capitalize font-mono">Tipo: {item.tipo}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
